import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import attendanceService from '../../../services/attendanceService';
import employeeService from '../../../services/employeeService';
import companyService from '../../../services/companyService';
import NotificationToast from '../../../components/ui/NotificationToast';
import { useReactToPrint } from 'react-to-print';

const DetailedAttendanceReport = ({ isAdmin }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    name: 'INCHI SOLUTIONS PRIVATE LIMITED',
    address: '#19/A, 3rd Floor, 1st Main Road, 3rd Block, 3rd Stage, Basaveshwara Nagar.',
    gstin: '29AAFCI9487D1Z2',
    logo_url: ''
  });
  const componentRef = useRef();

  useEffect(() => {
    loadEmployees();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    if (selectedEmployee && selectedMonth && selectedYear) {
      loadAttendanceData();
    }
  }, [selectedEmployee, selectedMonth, selectedYear]);

  const loadEmployees = async () => {
    try {
      const result = await employeeService.getEmployees();
      if (result?.success) {
        setEmployees(result.data || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const result = await companyService.getCompanyInfo();
      if (result?.success && result.data) {
        setCompanyInfo(prev => ({
          ...prev,
          name: result.data.name || prev.name,
          address: result.data.address || prev.address,
          gstin: result.data.tax_id || prev.gstin,
          logo_url: result.data.logo_url || ''
        }));
      }
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  const loadAttendanceData = async () => {
    if (!selectedEmployee) return;

    setLoading(true);
    try {
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

      // Fetch both raw records and summary
      const [recordsResult, summaryResult] = await Promise.all([
        attendanceService.getAttendanceRecords({
          employee_id: selectedEmployee,
          date_from: startDate,
          date_to: endDate
        }),
        attendanceService.getAttendanceSummary(selectedEmployee, startDate, endDate)
      ]);

      if (recordsResult?.success) {
        const records = recordsResult.data || [];
        const summaryFromBackend = summaryResult?.success ? summaryResult.data : null;
        const employee = employees.find(e => e.id === selectedEmployee);
        processAttendanceData(records, employee, selectedYear, selectedMonth, summaryFromBackend);
      }
    } catch (error) {
      setNotification({ message: 'Failed to load attendance data', type: 'error' });
      console.error('Error loading attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAttendanceData = (records, employee, year, month, summaryFromBackend) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const dailyData = {};
    const summary = summaryFromBackend ? {
      payableDays: parseFloat(summaryFromBackend.paid_days || 0),
      present: parseInt(summaryFromBackend.present || 0),
      absent: parseInt(summaryFromBackend.absent || 0),
      halfDays: parseInt(summaryFromBackend.half_days || 0),
      doublePresent: parseInt(summaryFromBackend.double_present || 0),
      weekOff: parseInt(summaryFromBackend.weekly_off || 0),
      paidLeaves: parseInt(summaryFromBackend.paid_leaves || 0),
      unpaidLeaves: parseInt(summaryFromBackend.unpaid_leaves || 0),
      publicHoliday: parseInt(summaryFromBackend.holidays || 0)
    } : {
      payableDays: daysInMonth,
      present: 0,
      absent: 0,
      halfDays: 0,
      doublePresent: 0,
      weekOff: 0,
      paidLeaves: 0,
      unpaidLeaves: 0,
      publicHoliday: 0
    };

    // Initialize all days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      
      // Weekly Off detection from schedule
      const weekOffDays = summaryFromBackend?.week_off_days || [0];
      const isWeekOff = weekOffDays.includes(dayOfWeek) || weekOffDays.includes(String(dayOfWeek));

      dailyData[day] = {
        date: date,
        dayName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
        status: isWeekOff ? 'WO' : '-',
        clockIn: '-',
        clockOut: '-',
        hoursWorked: '-',
        lateHours: '-',
        earlyHours: '-',
        overtimeHours: '-',
        isWeekOff: isWeekOff,
        isPublicHoliday: false
      };
    }

    // Process attendance records
    records.forEach(record => {
      const date = new Date(record.attendance_date);
      const day = date.getDate();
      const clockIn = record.clock_in_time ? new Date(record.clock_in_time) : null;
      const clockOut = record.clock_out_time ? new Date(record.clock_out_time) : null;

      // Use pre-calculated values from backend if available, fallback to frontend calculation
      let hoursWorked = record.work_hours ? parseFloat(record.work_hours) : 0;
      let lateHours = record.late_minutes ? parseFloat(record.late_minutes) / 60 : 0;
      let earlyHours = 0;
      let overtimeHours = record.overtime_hours ? parseFloat(record.overtime_hours) : 0;

      if (clockIn && clockOut && (hoursWorked === 0 || overtimeHours === 0)) {
        const dateStr = date.toISOString().split('T')[0];
        const workStart = employee?.work_start_time ? new Date(`${dateStr}T${employee.work_start_time}:00`) : null;
        const workEnd = employee?.work_end_time ? new Date(`${dateStr}T${employee.work_end_time}:00`) : null;

        if (hoursWorked === 0) {
          hoursWorked = (clockOut - clockIn) / (1000 * 60 * 60);
        }

        if (lateHours === 0 && workStart && clockIn > workStart) {
          lateHours = (clockIn - workStart) / (1000 * 60 * 60);
        }

        if (workEnd && clockOut < workEnd) {
          earlyHours = (workEnd - clockOut) / (1000 * 60 * 60);
        }

        // Only calculate overtime here if the record doesn't already have it
        if (overtimeHours === 0 && workEnd && clockOut > workEnd) {
          overtimeHours = (clockOut - workEnd) / (1000 * 60 * 60);
        }
      }

      // Determine status string for report
      let statusString = record.status || 'absent';
      let mappedStatus = '-';
      
      if (statusString === 'present') {
        mappedStatus = 'P';
        if (!summaryFromBackend) summary.present++;
      } else if (statusString === 'absent') {
        mappedStatus = 'A';
        if (!summaryFromBackend) summary.absent++;
      } else if (statusString === 'half_day') {
        mappedStatus = 'HD';
        if (!summaryFromBackend) summary.halfDays++;
      } else if (statusString === 'paid_leave') {
        mappedStatus = 'PL';
        if (!summaryFromBackend) summary.paidLeaves++;
      } else if (statusString === 'public_holiday' || statusString === 'holiday') {
        mappedStatus = 'PH';
        if (!summaryFromBackend) summary.publicHoliday++;
      } else if (statusString === 'marked_weekly_off' || statusString === 'weekly_off') {
        mappedStatus = 'WO';
        if (!summaryFromBackend) summary.weekOff++;
      }

      dailyData[day] = {
        ...dailyData[day],
        status: mappedStatus,
        clockIn: clockIn ? clockIn.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '-',
        clockOut: clockOut ? clockOut.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '-',
        hoursWorked: hoursWorked > 0 ? formatHours(hoursWorked) : '-',
        lateHours: lateHours > 0 ? formatHours(lateHours) : '-',
        earlyHours: earlyHours > 0 ? formatHours(earlyHours) : '-',
        overtimeHours: overtimeHours > 0 ? formatHours(overtimeHours) : '-'
      };
    });

    // Calculate totals - prefer summaryFromBackend for exact matches
    const totals = summaryFromBackend ? {
      hoursWorked: 0, // We'll still sum this from daily data as it's not in summary
      lateHours: 0,
      earlyHours: 0,
      overtimeHours: parseFloat(summaryFromBackend.overtime_hours || 0)
    } : {
      hoursWorked: 0,
      lateHours: 0,
      earlyHours: 0,
      overtimeHours: 0
    };

    Object.values(dailyData).forEach(day => {
      if (day.hoursWorked !== '-') {
        totals.hoursWorked += parseHours(day.hoursWorked);
      }
      if (day.lateHours !== '-') {
        totals.lateHours += parseHours(day.lateHours);
      }
      // Only sum overtime if we don't have a backend total
      if (!summaryFromBackend && day.overtimeHours !== '-') {
        totals.overtimeHours += parseHours(day.overtimeHours);
      }
    });

    // Get most common client site from records
    const clientSiteCounts = {};
    records.forEach(record => {
      if (record.client_site_name) {
        const key = `${record.client_site_name} - ${record.client_name || ''}`;
        clientSiteCounts[key] = (clientSiteCounts[key] || 0) + 1;
      }
    });
    const mostCommonClientSite = Object.keys(clientSiteCounts).length > 0
      ? Object.keys(clientSiteCounts).reduce((a, b) => 
          clientSiteCounts[a] > clientSiteCounts[b] ? a : b, '')
      : null;

    setAttendanceData({
      employee,
      summary,
      dailyData,
      totals,
      clientSite: mostCommonClientSite,
      month,
      year,
      monthName: new Date(year, month - 1).toLocaleString('default', { month: 'long' })
    });
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const parseHours = (timeStr) => {
    if (timeStr === '-') return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h + m / 60;
  };

  const formatTotalHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Attendance_Report_${selectedYear}_${String(selectedMonth).padStart(2, '0')}`,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 0.5cm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .attendance-report {
          font-family: "Gill Sans MT", sans-serif;
          color: #333;
        }
        .attendance-report table {
          page-break-inside: avoid;
          font-size: 9px;
        }
      }
    `,
    onBeforeGetContent: () => {
      // Ensure component is ready
      return Promise.resolve();
    }
  });

  const handleSavePDF = () => {
    if (!componentRef.current) {
      setNotification({ message: 'Report content not ready. Please wait...', type: 'error' });
      return;
    }
    if (!attendanceData) {
      setNotification({ message: 'No attendance data to print', type: 'error' });
      return;
    }
    try {
      handlePrint();
    } catch (error) {
      console.error('Print error:', error);
      setNotification({ message: 'Failed to open print dialog. Please try again.', type: 'error' });
    }
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i).toLocaleString('default', { month: 'long' })
  }));

  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - 5 + i;
    return { value: year, label: String(year) };
  });

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      {/* Controls */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Employee</label>
              <Select
                value={selectedEmployee || ''}
                onValueChange={setSelectedEmployee}
                placeholder="Select Employee"
                options={[
                  { value: '', label: 'Select Employee' },
                  ...(employees.map(emp => ({
                    value: emp.id,
                    label: `${emp.full_name} (${emp.employee_id})`
                  })) || [])
                ]}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Month</label>
            <Select
              value={selectedMonth}
              onValueChange={(value) => setSelectedMonth(Number(value))}
              options={monthOptions}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Year</label>
            <Select
              value={selectedYear}
              onValueChange={(value) => setSelectedYear(Number(value))}
              options={yearOptions}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleSavePDF}
              disabled={!attendanceData || loading}
              className="w-full"
              iconName="Printer"
            >
              Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Report */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading attendance data...</p>
        </div>
      ) : attendanceData ? (
        <div className="bg-white print:bg-white">
          <div className="attendance-report p-8" style={{ fontFamily: '"Gill Sans MT", sans-serif' }} ref={componentRef}>
            {/* Header */}
            <div className="mb-6 flex items-start">
              <div className="w-40 h-32 mr-4 flex items-center justify-center bg-white border border-gray-200">
                {companyInfo.logo_url ? (
                  <img
                    src={`${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || `http://${window.location.hostname}:8000`}${companyInfo.logo_url}`}
                    alt="Company Logo"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.logo-placeholder');
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`logo-placeholder w-full h-full bg-gray-200 flex items-center justify-center ${companyInfo.logo_url ? 'hidden' : ''}`}>
                  <span className="text-sm text-gray-500">LOGO</span>
                </div>
              </div>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-gray-800 mb-1">{companyInfo.name}</h1>
                <h2 className="text-base font-semibold text-gray-800 mb-2">
                  ATTENDANCE REPORT FOR THE MONTH of {attendanceData.monthName} {attendanceData.year}
                </h2>
                <div className="text-xs text-gray-700 space-y-1">
                  <p>Report Date: {new Date().toLocaleDateString('en-GB')}</p>
                  <p>Address: {companyInfo.address}</p>
                  <p>Gstin: {companyInfo.gstin}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-300 my-4"></div>

            {/* Employee Details */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Employee Details</h3>
              <table className="w-full border-collapse border border-gray-300 text-xs">
                <thead>
                  <tr className="bg-gray-300">
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Name</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Phone Number</th>
                    <th className="border border-gray-300 px-2 py-1 text-right font-bold">Branch</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Department</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Employee Id</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Designation</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Schedule</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Scheduled Working Hours</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Client Site</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.employee?.full_name || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.employee?.phone || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-right">{attendanceData.employee?.branch || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.employee?.department || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.employee?.employee_id || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.employee?.position || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">-</td>
                    <td className="border border-gray-300 px-2 py-1">
                      {attendanceData.employee?.work_start_time && attendanceData.employee?.work_end_time
                        ? `${attendanceData.employee.work_start_time} - ${attendanceData.employee.work_end_time}`
                        : '-'}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.clientSite || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Attendance Summary */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Attendance summary</h3>
              <table className="w-full border-collapse border border-gray-300 text-xs">
                <thead>
                  <tr className="bg-gray-300">
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Payable Days</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Present</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Absent</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Half Days</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Double Present</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Week Off</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Paid Leaves</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Unpaid Leaves</th>
                    <th className="border border-gray-300 px-2 py-1 text-left font-bold">Public Holiday</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.payableDays.toFixed(1)}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.present}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.absent}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.halfDays}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.doublePresent}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.weekOff}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.paidLeaves}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.unpaidLeaves}</td>
                    <td className="border border-gray-300 px-2 py-1">{attendanceData.summary.publicHoliday}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Daily Calendar */}
            <div className="mb-4 overflow-x-auto">
              <table className="w-full border-collapse border border-gray-500 text-xs" style={{ minWidth: '1000px' }}>
                <thead>
                  <tr className="bg-gray-400">
                    <th colSpan="2" className="border border-gray-500 px-1 py-1 text-center font-bold">Date</th>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      const date = new Date(attendanceData.year, attendanceData.month - 1, day);
                      return (
                        <th key={day} className="border border-gray-500 px-1 py-1 text-center font-bold">
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).replace(' ', ' ')}
                        </th>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">Day</td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.dayName || '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">Status</td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.status || '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td rowSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">Punch Timings</td>
                    <td className="border border-gray-300 px-1 py-1 text-left font-bold">In Out</td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      const data = attendanceData.dailyData[day];
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          <div>{data?.clockIn || '-'}</div>
                          <div>{data?.clockOut || '-'}</div>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-1 py-1 text-left font-bold">Hrs Worked</td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.hoursWorked || '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">
                      Hrs Worked Total: <span className="font-normal">{formatTotalHours(attendanceData.totals.hoursWorked)}</span>
                    </td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.hoursWorked !== '-' ? attendanceData.dailyData[day].hoursWorked : '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">
                      Late Hrs Total: <span className="font-normal">{formatTotalHours(attendanceData.totals.lateHours)}</span>
                    </td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.lateHours !== '-' ? attendanceData.dailyData[day].lateHours : '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">Early Hrs Total:</td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">-</td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td colSpan="2" className="border border-gray-300 px-1 py-1 text-left font-bold">
                      Overtime Hrs Total: <span className="font-normal">{formatTotalHours(attendanceData.totals.overtimeHours)}</span>
                    </td>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = i + 1;
                      const daysInMonth = Object.keys(attendanceData.dailyData).length;
                      if (day > daysInMonth) return null;
                      return (
                        <td key={day} className="border border-gray-300 px-1 py-1 text-center text-xs">
                          {attendanceData.dailyData[day]?.overtimeHours !== '-' ? attendanceData.dailyData[day].overtimeHours : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </thead>
              </table>
            </div>

            {/* Notes */}
            <div className="border border-gray-300 p-2 text-xs">
              <p className="font-bold text-gray-800">
                Note: <span className="font-normal">
                  Present: P, Absent: A, Paid leave: PL, Unpaid leave: UPL, Half Day: HD, Week off: WO, Public holiday: PH, Half-day paid leave: HD/PL, Double present: 2P
                </span>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Select an employee and month to generate attendance report</p>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .attendance-report {
            font-family: "Gill Sans MT", sans-serif;
            color: #333;
            width: 100%;
          }
          .attendance-report table {
            page-break-inside: avoid;
          }
        }
        @media screen {
          .attendance-report {
            font-family: "Gill Sans MT", sans-serif;
            color: #333;
          }
        }
      `}</style>
    </div>
  );
};

export default DetailedAttendanceReport;

